import {
  CONTENT_CATEGORY_LABEL,
  ContentCategoryId,
} from '@/constants/content-categoryId';
import { parseTagList } from '@/utils/tag-slug';
import { fetchAllFromCache, fetchFromCache, FetchAPIOptions } from './api';
import {
  fetchAllFromWordpressAndCache,
  fetchFromWordpressAndCache,
} from './wordpress-api';
import {
  BLOG_NEWS_ITEM_QUERY_BY_SLUG,
  BLOG_NEWS_LIST_QUERY,
} from './wordpress-queries';

// ============================================
// Types
// ============================================

/**
 * 리스트용 경량 타입 (목록 페이지에서 사용)
 */
export interface BlogNewsListItemProp {
  id: string;
  title: string;
  databaseId: number;
  viewCount: number;
  slug: string;
  /**
   * WordPress native featured_media → CDN URL. content-studio 로 발행된 포스트는
   * media library 에 등록되지 않아 비어 있을 수 있음 — fallback 으로 `heroImage`
   * (ACF) 사용.
   */
  featuredImage: string;
  /**
   * content-studio 로 발행된 포스트의 hero 이미지 — ACF `heroimage` 필드.
   * CloudFront URL. WP media library 우회용. legacy 포스트는 빈 문자열.
   */
  heroImage: string;
  lang: string[];
  categoryEn: string[] | null;
  categoryKo: string[] | null;
  date: string;
  authorId: string;
  /** 소속 시리즈 slug (ACF `blog.series`). 시리즈물이 아니면 빈 문자열/undefined. */
  series?: string;
  /** 태그 slug 배열 (ACF `blog.tags` 콤마 문자열을 정규화). 태그 없으면 빈 배열. */
  tags?: string[];
}

/**
 * 상세 페이지용 전체 타입 (개별 포스트 페이지에서 사용)
 */
export interface BlogNewsProp extends BlogNewsListItemProp {
  // Extended fields (for detail page)
  content?: string;
  /**
   * WordPress `modified` 타임스탬프 — jsonLd BlogPosting.dateModified 머지에 사용.
   */
  modified?: string;
  /**
   * Content Studio 가 생성해 ACF `jsonLd` 필드로 전달한 구조화 데이터(@graph) 문자열.
   * 파생물이라 비어 있을 수 있음 — 없으면 `generateBlogPostingJsonLd()` fallback.
   */
  jsonLd?: string;
  /** Open Graph 전용 제목/설명 (ACF). 비어 있으면 seoTitle·seoMetaDesc 로 fallback. */
  ogTitle?: string;
  ogDescription?: string;
  // Popup
  popupEnable?: boolean;
  popupButton?: string;
  popupImage?: string;
  popupLink?: string;
  popupTitle?: string;
  popupText?: string;
  // SEO
  seoMetaDesc?: string;
  seoOpengraphImage?: string;
  seoTitle?: string;
}

// ============================================
// List Fetch Functions (경량 데이터)
// ============================================

/**
 * WordPress에서 리스트용 포스트를 가져와 S3에 캐시 (refresh용)
 */
const fetchAllListPostsFromWordpress = async (
  categoryId: ContentCategoryId,
  label: string,
  prodFilter?: boolean,
) => {
  return fetchAllFromWordpressAndCache(
    {
      query: BLOG_NEWS_LIST_QUERY,
      getVariables: cursor => ({
        categoryId,
        postCount: 50,
        after: cursor,
      }),
      getPageInfo: data => ({
        hasNextPage: data?.posts?.pageInfo?.hasNextPage || false,
        endCursor: data?.posts?.pageInfo?.endCursor || null,
      }),
      getItems: data => data?.posts?.edges || [],
    },
    { label, prodFilter },
  );
};

/**
 * S3 캐시에서 리스트용 포스트를 가져옴 (빌드/런타임용)
 */
const fetchAllListPostsFromCache = async (label: string, strict?: boolean) => {
  return fetchAllFromCache(
    {
      getPageInfo: data => ({
        hasNextPage: data?.posts?.pageInfo?.hasNextPage || false,
      }),
      getItems: data => data?.posts?.edges || [],
      postCount: 50,
    },
    { label, strict },
  );
};

/**
 * 리스트용 경량 포스트 목록 조회
 * - 리스트 페이지에서 사용 (카테고리, 검색, 작성자 페이지 등)
 * - content, author details, popup, seo 필드 제외
 */
export async function getBlogNewsPostsForList(
  categoryId: ContentCategoryId,
  options?: FetchAPIOptions,
): Promise<BlogNewsListItemProp[]> {
  try {
    const label = `${options?.label ? options.label + '-' : ''}getBlogNewsPostsForList-${CONTENT_CATEGORY_LABEL[categoryId]}`;
    const refresh = options?.refresh ?? false;

    const allPostEdges = refresh
      ? await fetchAllListPostsFromWordpress(
          categoryId,
          label,
          options?.prodFilter,
        )
      : await fetchAllListPostsFromCache(label, options?.strict);

    return (
      allPostEdges?.map((edge: any) => ({
        id: edge.node.id,
        title: edge.node.title,
        databaseId: edge.node.databaseId,
        viewCount: edge.node.viewCount,
        slug: edge.node.slug,
        lang: edge.node.blog.lang,
        categoryKo: edge.node.blog.categoryKo,
        categoryEn: edge.node.blog.categoryEn,
        date: edge.node.blog.date,
        featuredImage: edge.node.featuredImage?.node.mediaItemUrl,
        heroImage: edge.node.blog.heroimage ?? '',
        authorId: edge.node.blog.author2?.nodes[0]?.author?.id,
        series: edge.node.blog.series ?? '',
        tags: parseTagList(edge.node.blog.tags),
      })) || []
    );
  } catch (error) {
    // strict 호출자는 이 목록으로 다른 캐시를 만든다 — 삼켜서 빈 배열을 주면 "글이 없다"로
    // 오해돼 틀린 집계가 영속화된다
    if (options?.strict) throw error;
    return [];
  }
}

// ============================================
// Individual Post Fetch Functions (개별 상세 데이터)
// ============================================

/**
 * slug를 기반으로 캐시 키 생성
 */
const getPostCacheKey = (slug: string, categoryId: ContentCategoryId) => {
  return `getBlogNewsPost-${CONTENT_CATEGORY_LABEL[categoryId]}-${slug}`;
};

/**
 * WordPress GraphQL 원본 포스트 노드(`data.post`)를 상세 페이지 응답 형태(BlogNewsProp)로
 * 정규화한다.
 *
 * 상세 조회(`getBlogNewsPostBySlug`)와 저장 훅(wordpress-webhook.ts)이 이 정규화를
 * 공유한다 — 각자 구현하면 GraphQL 스키마 변경 시 두 곳을 고쳐야 한다.
 */
export function toBlogNewsProp(post: any): BlogNewsProp {
  return {
    id: post.id,
    title: post.title,
    databaseId: post.databaseId,
    viewCount: post.viewCount,
    slug: post.slug,
    content: post.content,
    modified: post.modified,
    jsonLd: post.blog?.jsonLd,
    ogTitle: post.blog?.ogTitle,
    ogDescription: post.blog?.ogDescription,
    lang: post.blog?.lang,
    categoryKo: post.blog?.categoryKo,
    categoryEn: post.blog?.categoryEn,
    date: post.blog?.date,
    featuredImage: post.featuredImage?.node?.mediaItemUrl,
    heroImage: post.blog?.heroimage ?? '',
    // 상세 페이지가 시리즈·태그 캐시를 찾는 키. 리스트 매퍼와 같은 정규화를 쓴다
    // (tags 는 ACF 콤마 문자열이라 parseTagList 를 거쳐야 slug 배열이 된다)
    series: post.blog?.series ?? '',
    tags: parseTagList(post.blog?.tags),
    // 저자는 slug(=저자 캐시 조회 키)만 담는다 — 프로필은 저자 캐시(getAuthorData) 단일 출처
    authorId: post.blog?.author2?.nodes[0]?.author?.id,
    // Popup
    popupEnable: post.popup?.popupEnable,
    popupButton: post.popup?.popupButton,
    popupImage: post.popup?.popupImage,
    popupLink: post.popup?.popupLink,
    popupTitle: post.popup?.popupTitle,
    popupText: post.popup?.popupText,
    // SEO
    seoMetaDesc: post.seo?.metaDesc,
    seoOpengraphImage: post.seo?.opengraphImage?.link,
    seoTitle: post.seo?.title,
  };
}

/**
 * 개별 포스트 상세 조회 (slug 기반)
 * - 상세 페이지에서 사용
 * - 개별 캐시 적용
 */
export async function getBlogNewsPostBySlug(
  slug: string,
  categoryId: ContentCategoryId,
  options?: FetchAPIOptions,
): Promise<BlogNewsProp | null> {
  try {
    const label = getPostCacheKey(slug, categoryId);
    const refresh = options?.refresh ?? false;

    let data: any;

    if (refresh) {
      data = await fetchFromWordpressAndCache(
        BLOG_NEWS_ITEM_QUERY_BY_SLUG,
        {
          variables: {
            slug,
            categoryId,
          },
        },
        { label, prodFilter: options?.prodFilter },
      );
    } else {
      data = await fetchFromCache(label);
    }

    // 카테고리 스코프 쿼리는 posts.nodes[0] 로 반환. 구 캐시(post) 하위호환.
    const post = data?.posts?.nodes?.[0] ?? data?.post;
    if (!post) return null;

    return toBlogNewsProp(post);
  } catch (error) {
    return null;
  }
}
